import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import {
  Play, Bell, ChevronDown, ChevronRight, Building2,
  CalendarDays, FileText, Clock, AlertTriangle, ScanLine,
  Zap, BookOpen, MonitorPlay, X
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const PRACTICE = {
  name: 'Riverside Medical Group',
  npi: '1234567890',
  city: 'Austin, TX 78704',
  phone: '(512) 555-0190',
  specialty: 'Internal Medicine',
  providers: 4,
  locations: 2,
};

const DEFAULT_USER = {
  name: 'Dr. Sarah Chen',
  role: 'Practice Administrator',
  initials: 'SC',
  lastLogin: 'Today at 8:42 AM',
};

const ALERTS = [
  { text: '2 unsigned Lumina notes', urgent: true },
  { text: '1 high no-show risk (James Taylor, 11 AM)', urgent: true },
  { text: 'RevQ denial rate up 0.8% this week', urgent: true },
];

const VIDEO_TIERS = [
  { id: 'quick', label: 'Quick Overview', time: '1 min', icon: Zap, desc: 'Making healthcare easier for everyone — see Echo in action', color: 'border-emerald-300 bg-emerald-50', videoId: 'fFeW5dx7KgA' },
  { id: 'standard', label: 'Patient Experience', time: '1 min', icon: Clock, desc: 'How Echo puts patients back in control of their care', color: 'border-blue-300 bg-blue-50', videoId: 'ZgclxG5XTkU' },
  { id: 'deep', label: 'AI Scheduling Demo', time: '1 min', icon: BookOpen, desc: 'Scheduling care through a conversational AI agent with Echo', color: 'border-violet-300 bg-violet-50', videoId: 'fgYhfipy_NY' },
];

const VideoTourDialog = ({ open, onOpenChange }) => {
  const [activeTier, setActiveTier] = useState(null);
  const handleClose = () => { setActiveTier(null); onOpenChange(false); };
  const handleBack = () => setActiveTier(null);

  if (activeTier) {
    const tier = VIDEO_TIERS.find(t => t.id === activeTier);
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden gap-0" aria-describedby={undefined} data-testid="video-tour-player">
          <div className="bg-black px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={handleBack} className="text-white/60 hover:text-white transition-colors text-xs font-medium flex items-center gap-1" data-testid="video-back-btn">
                <ChevronRight className="h-3 w-3 rotate-180" /> Back
              </button>
              <div className="h-4 w-px bg-white/20" />
              <DialogTitle className="text-sm font-medium text-white flex items-center gap-2">
                <MonitorPlay className="h-4 w-4" /> {tier.label}
              </DialogTitle>
              <Badge className="bg-white/15 text-white/70 border-0 text-[9px]">{tier.time}</Badge>
            </div>
          </div>
          <div className="aspect-video bg-black">
            <iframe src={`https://www.youtube.com/embed/${tier.videoId}?autoplay=1&rel=0`} title={tier.label} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" data-testid="video-tour-iframe" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg p-0 overflow-hidden" aria-describedby={undefined} data-testid="video-tour-dialog">
        <div className="bg-slate-950 px-6 pt-6 pb-4 text-white">
          <DialogTitle className="text-lg font-medium flex items-center gap-2">
            <Play className="h-5 w-5" /> Video Tour — AriaOne Platform
          </DialogTitle>
          <p className="text-sm text-slate-400 mt-1">Choose a tour depth. Each video walks you through the platform at your pace.</p>
        </div>
        <div className="p-6 space-y-3">
          {VIDEO_TIERS.map(tier => {
            const Icon = tier.icon;
            return (
              <button key={tier.id} onClick={() => setActiveTier(tier.id)} className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all hover:shadow-md ${tier.color}`} data-testid={`video-tier-${tier.id}`}>
                <div className="h-12 w-12 rounded-xl bg-white/80 flex items-center justify-center shrink-0"><Icon className="h-6 w-6 text-slate-700" /></div>
                <div className="flex-1">
                  <div className="flex items-center gap-2"><p className="text-sm font-medium text-slate-800">{tier.label}</p><Badge className="bg-white/60 text-slate-600 text-[9px] border-0">{tier.time}</Badge></div>
                  <p className="text-xs text-slate-600 mt-0.5">{tier.desc}</p>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-400" />
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const LUMINA_LOGO_BLACK = "https://customer-assets.emergentagent.com/job_revenue-cycle-1/artifacts/rms1wglh_lumina_logo_black.png";
const REVQ_LOGO_BLACK = "https://customer-assets.emergentagent.com/job_medcycle-metrics/artifacts/zakm44og_revQ_logo_black.png";
const ECHO_LOGO_BLACK = "https://customer-assets.emergentagent.com/job_medcycle-metrics/artifacts/r0ngtnwn_echo_logo_black.svg";

const APP_CARDS = [
  {
    id: 'echo',
    name: 'echo',
    logo: ECHO_LOGO_BLACK,
    dotColor: '#3b82f6',
    statusBadge: { label: 'Active', bg: '#f0fdf4', color: '#166534' },
    tiles: [
      { value: '24', color: '#111111', label: 'Scheduled' },
      { value: '8', color: '#111111', label: 'Checked in' },
      { value: '2', color: '#d97706', label: 'No-show risk' },
    ],
    category: 'Front Office Operations',
    description: 'Smart scheduling, AI phone agents, patient flow, and Document Lens.',
    href: '/echo',
    launchLabel: 'Launch Echo',
  },
  {
    id: 'lumina',
    name: 'lumina',
    logo: LUMINA_LOGO_BLACK,
    dotColor: '#6b5ce7',
    statusBadge: { label: '2 unsigned', bg: '#faf0ff', color: '#6b21a8' },
    tiles: [
      { value: '7', color: '#111111', label: 'Notes today' },
      { value: '3', color: '#ef4444', label: 'Pending sign' },
      { value: '247', color: '#111111', label: 'Cr balance' },
    ],
    category: 'Clinical Intelligence',
    description: 'Ambient listening, AI SOAP notes, auto-coding, and Lumina Lens.',
    href: '/lumina',
    launchLabel: 'Launch Lumina',
  },
  {
    id: 'revq',
    name: 'revQ',
    logo: REVQ_LOGO_BLACK,
    dotColor: '#22c55e',
    statusBadge: { label: 'Active', bg: '#f0fdf4', color: '#166534' },
    tiles: [
      { value: '12', color: '#d97706', label: 'Open claims' },
      { value: '28', color: '#111111', label: 'Avg days A/R' },
      { value: '3.2%', color: '#ef4444', label: 'Denial rate' },
    ],
    category: 'Revenue Cycle Management',
    description: 'AI-powered billing analytics, claims processing, and denial management.',
    href: '/revq',
    launchLabel: 'Launch revQ',
  },
];

const METRICS = [
  { icon: CalendarDays, label: 'Patients today', value: '24', sub: '8 remaining', subColor: '#aaaaaa', borderColor: '#e8e8e8', iconColor: '#aaaaaa', valueColor: '#111111' },
  { icon: FileText, label: 'Claims pending', value: '8', sub: 'Avg 22 days in A/R', subColor: '#aaaaaa', borderColor: '#e8e8e8', iconColor: '#aaaaaa', valueColor: '#111111' },
  { icon: Clock, label: 'Collections MTD', value: '$48.5k', sub: '+$3.2k vs last month', subColor: '#22c55e', borderColor: '#e8e8e8', iconColor: '#aaaaaa', valueColor: '#111111' },
  { icon: AlertTriangle, label: 'Denial rate', value: '3.2%', sub: 'Up 0.8% this week', subColor: '#ef4444', borderColor: '#fde8e8', iconColor: '#ef4444', valueColor: '#ef4444' },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [showVideoTour, setShowVideoTour] = useState(false);
  const [lensTooltip, setLensTooltip] = useState(null);

  const USER = {
    name: authUser?.name || [authUser?.first_name, authUser?.last_name].filter(Boolean).join(' ') || DEFAULT_USER.name,
    role: authUser?.role || DEFAULT_USER.role,
    initials: authUser?.first_name && authUser?.last_name
      ? `${authUser.first_name[0]}${authUser.last_name[0]}`.toUpperCase()
      : DEFAULT_USER.initials,
    lastLogin: DEFAULT_USER.lastLogin,
  };

  const alertCount = ALERTS.length;
  const alertText = ALERTS.map(a => a.text).join(' · ');

  // Check setup completion
  const setupProgress = (() => {
    try { const s = JSON.parse(localStorage.getItem('ariaone-setup-progress') || '{}'); return s.progress || 0; }
    catch { return 0; }
  })();
  const setupIncomplete = setupProgress > 0 && setupProgress < 100;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#f5f5f5', fontFamily: 'system-ui, Inter, sans-serif' }} data-testid="landing-page">

      {/* ━━━ 1. TOP BAR ━━━ */}
      <header className="w-full flex-shrink-0" style={{ background: '#111111', height: 48 }} data-testid="top-bar">
        <div className="h-full flex items-center justify-between" style={{ padding: '0 24px' }}>
          <div className="flex items-center">
            <img src="/ariaone_logo_white.png" alt="AriaOne" className="h-5 w-auto" data-testid="ariaone-logo" />
            <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.15)', margin: '0 12px' }} />
            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, letterSpacing: '0.06em', fontWeight: 400 }}>HEALTHCARE PLATFORM</span>
          </div>
          <div className="flex items-center" style={{ gap: 8 }}>
            <button
              onClick={() => setShowVideoTour(true)}
              className="hidden sm:flex items-center"
              style={{ background: 'rgba(255,255,255,0.07)', border: '0.5px solid rgba(255,255,255,0.15)', borderRadius: 6, height: 28, padding: '0 12px', fontSize: 12, color: 'rgba(255,255,255,0.75)', cursor: 'pointer', gap: 5 }}
              data-testid="video-tour-btn"
            >
              <Play className="h-3 w-3" style={{ fill: 'rgba(255,255,255,0.75)' }} /> Video Tour
            </button>
            <button
              className="relative flex items-center justify-center"
              style={{ background: 'rgba(255,255,255,0.07)', border: '0.5px solid rgba(255,255,255,0.15)', borderRadius: 6, width: 28, height: 28, cursor: 'pointer' }}
              data-testid="notifications-btn"
            >
              <Bell className="h-3.5 w-3.5" style={{ color: 'rgba(255,255,255,0.75)' }} />
              <span className="absolute" style={{ top: -2, right: -2, width: 6, height: 6, borderRadius: '50%', background: '#ef4444' }} />
            </button>
            <div style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.1)' }} />
            <div className="flex items-center" style={{ gap: 8 }}>
              <div className="flex items-center justify-center" style={{ width: 26, height: 26, borderRadius: '50%', background: '#6b5ce7', color: '#ffffff', fontSize: 10, fontWeight: 500 }} data-testid="user-avatar">
                {USER.initials}
              </div>
              <div style={{ lineHeight: 1 }} className="hidden sm:block">
                <p style={{ color: '#ffffff', fontSize: 12, fontWeight: 500, margin: 0 }}>{USER.name}</p>
                <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, margin: '2px 0 0', fontWeight: 400 }}>{USER.role}</p>
              </div>
              <ChevronDown className="h-2.5 w-2.5" style={{ color: 'rgba(255,255,255,0.3)' }} />
            </div>
          </div>
        </div>
      </header>

      {/* ━━━ 2. PRACTICE BAR ━━━ */}
      <div className="flex-shrink-0 flex flex-col sm:flex-row sm:items-center justify-between" style={{ background: '#ffffff', borderBottom: '0.5px solid #e8e8e8', minHeight: 52, padding: '8px 24px' }} data-testid="practice-bar">
        <div className="flex items-center" style={{ gap: 10 }}>
          <div className="flex items-center justify-center flex-shrink-0" style={{ width: 32, height: 32, background: '#f0f0f0', borderRadius: 8 }}>
            <Building2 className="h-4 w-4" style={{ color: '#888888' }} />
          </div>
          <div>
            <p style={{ fontSize: 14, fontWeight: 500, color: '#111111', margin: 0, lineHeight: 1.2 }} data-testid="practice-name">{PRACTICE.name}</p>
            <div className="flex items-center flex-wrap" style={{ gap: 0, marginTop: 2 }}>
              <span style={{ fontSize: 11, color: '#888888' }}>NPI {PRACTICE.npi}</span>
              <span style={{ fontSize: 11, color: '#cccccc', margin: '0 6px' }}>·</span>
              <span style={{ fontSize: 11, color: '#888888' }}>{PRACTICE.city}</span>
              <span className="hidden sm:inline" style={{ fontSize: 11, color: '#cccccc', margin: '0 6px' }}>·</span>
              <span className="hidden sm:inline" style={{ fontSize: 11, color: '#888888' }}>{PRACTICE.phone}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center flex-wrap mt-2 sm:mt-0" style={{ gap: 8 }}>
          {[PRACTICE.specialty, `${PRACTICE.providers} Providers`, `${PRACTICE.locations} Locations`].map(tag => (
            <span key={tag} className="flex items-center justify-center" style={{ height: 26, padding: '0 10px', borderRadius: 20, border: '0.5px solid #e0e0e0', background: '#ffffff', fontSize: 11, color: '#555555' }}>
              {tag}
            </span>
          ))}
        </div>
      </div>

      {/* ━━━ 3. SMART ALERT BAR ━━━ */}
      {alertCount > 0 && (
        <div className="flex items-center" style={{ background: '#ffffff', border: '0.5px solid #fde8e8', borderRadius: 10, padding: '10px 16px', margin: '24px 24px 0', gap: 10 }} data-testid="smart-alert-bar">
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#ef4444', flexShrink: 0 }} />
          <p style={{ fontSize: 12, color: '#555555', margin: 0, flex: 1 }}>
            <span style={{ fontWeight: 500, color: '#111111' }}>{alertCount} items need attention today — </span>
            {alertText}
          </p>
          <button onClick={() => toast.info('Review all coming soon')} style={{ fontSize: 12, color: '#6b5ce7', cursor: 'pointer', whiteSpace: 'nowrap', marginLeft: 'auto', background: 'none', border: 'none', fontWeight: 400 }} data-testid="review-all-btn">
            Review all →
          </button>
        </div>
      )}

      {/* ━━━ SETUP INCOMPLETE BANNER ━━━ */}
      {setupIncomplete && (
        <div
          className="flex items-center"
          style={{ background: '#fffbeb', border: '0.5px solid #fde68a', borderRadius: 10, padding: '10px 16px', margin: '12px 24px 0', gap: 10, cursor: 'pointer' }}
          onClick={() => navigate('/lumina/settings')}
          data-testid="setup-incomplete-banner"
        >
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#d97706', flexShrink: 0 }} />
          <p style={{ fontSize: 12, color: '#92400e', flex: 1, margin: 0 }}>
            <span style={{ fontWeight: 500 }}>Account setup is {setupProgress}% complete.</span>
            <span style={{ color: '#b45309', marginLeft: 4 }}>Finish setup in Settings to unlock full functionality.</span>
          </p>
          <span style={{ fontSize: 11, color: '#d97706', fontWeight: 500, flexShrink: 0 }}>Complete setup →</span>
        </div>
      )}

      {/* ━━━ 4. METRICS ROW ━━━ */}
      <div className="grid grid-cols-2 md:grid-cols-4" style={{ gap: 10, padding: '20px 24px 0' }} data-testid="metrics-row">
        {METRICS.map((m, i) => {
          const Icon = m.icon;
          return (
            <div key={i} style={{ background: '#ffffff', border: `0.5px solid ${m.borderColor}`, borderRadius: 10, padding: '14px 16px' }} data-testid={`metric-${i}`}>
              <div className="flex items-center" style={{ gap: 6, marginBottom: 4 }}>
                <Icon style={{ width: 14, height: 14, color: m.iconColor }} />
                <span style={{ fontSize: 11, color: '#aaaaaa', fontWeight: 400 }}>{m.label}</span>
              </div>
              <p style={{ fontSize: 24, fontWeight: 500, color: m.valueColor, lineHeight: 1, margin: 0 }}>{m.value}</p>
              <p style={{ fontSize: 11, color: m.subColor, marginTop: 3, margin: '3px 0 0', fontWeight: 400 }}>{m.sub}</p>
            </div>
          );
        })}
      </div>

      {/* ━━━ 5. SECTION HEADING ━━━ */}
      <div className="flex items-baseline" style={{ gap: 10, padding: '20px 24px 16px' }}>
        <span style={{ fontSize: 14, fontWeight: 500, color: '#111111' }} data-testid="module-selector-title">Your applications</span>
        <span style={{ fontSize: 12, color: '#aaaaaa', fontWeight: 400 }}>Select a module to get started</span>
      </div>

      {/* ━━━ 6. APPLICATION CARDS ━━━ */}
      <div className="grid grid-cols-1 md:grid-cols-3" style={{ gap: 14, padding: '0 24px 24px' }} data-testid="app-cards-grid">
        {APP_CARDS.map(app => (
          <div
            key={app.id}
            className="group"
            style={{ background: '#ffffff', border: '0.5px solid #e8e8e8', borderRadius: 14, overflow: 'hidden', cursor: 'pointer', transition: 'border-color 180ms, transform 180ms' }}
            onClick={() => navigate(app.href)}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#6b5ce7'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#e8e8e8'; e.currentTarget.style.transform = 'translateY(0)'; }}
            data-testid={`${app.id}-card`}
          >
            {/* APP HEADER */}
            <div style={{ padding: '18px 20px 14px' }}>
              {/* Logo row */}
              <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
                <div className="flex items-center" style={{ gap: 8 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: app.dotColor, flexShrink: 0 }} />
                  <img src={app.logo} alt={app.name} style={{ height: 22, width: 'auto', objectFit: 'contain' }} data-testid={`${app.id}-logo`} />
                </div>
                <span style={{ fontSize: 10, fontWeight: 500, padding: '2px 7px', borderRadius: 20, background: app.statusBadge.bg, color: app.statusBadge.color }} data-testid={`${app.id}-badge`}>
                  {app.statusBadge.label}
                </span>
              </div>
              {/* Data tiles */}
              <div className="flex" style={{ gap: 8 }}>
                {app.tiles.map((tile, j) => (
                  <div key={j} style={{ background: '#f8f8f8', borderRadius: 6, padding: '6px 10px', flex: 1 }} data-testid={`${app.id}-tile-${j}`}>
                    <p style={{ fontSize: 16, fontWeight: 500, color: tile.color, lineHeight: 1, margin: 0 }}>{tile.value}</p>
                    <p style={{ fontSize: 10, color: '#aaaaaa', marginTop: 1, margin: '1px 0 0', fontWeight: 400 }}>{tile.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div style={{ height: 0.5, background: '#f0f0f0' }} />

            {/* APP BODY */}
            <div style={{ padding: '16px 20px' }}>
              <p style={{ fontSize: 10, color: '#aaaaaa', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6, fontWeight: 400 }}>{app.category}</p>
              <p style={{ fontSize: 12, color: '#555555', lineHeight: 1.5, marginBottom: 14, fontWeight: 400 }}>{app.description}</p>
              {/* Action row */}
              <div className="flex" style={{ gap: 8 }}>
                <button
                  onClick={e => { e.stopPropagation(); navigate(app.href); }}
                  className="flex items-center justify-between"
                  style={{ flex: 1, height: 36, borderRadius: 8, background: '#111111', color: '#ffffff', fontSize: 13, fontWeight: 500, border: 'none', cursor: 'pointer', padding: '0 14px', transition: 'background 150ms' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#222222'}
                  onMouseLeave={e => e.currentTarget.style.background = '#111111'}
                  data-testid={`launch-${app.id}`}
                >
                  <span>{app.launchLabel}</span>
                  <span style={{ opacity: 0.5 }}>→</span>
                </button>
                <div className="relative">
                  <button
                    onClick={e => { e.stopPropagation(); navigate('/onboarding'); }}
                    className="flex items-center justify-center"
                    style={{ width: 36, height: 36, borderRadius: 8, border: '0.5px solid #e0e0e0', background: '#ffffff', cursor: 'pointer', transition: 'border-color 150ms' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#6b5ce7'; setLensTooltip(app.id); }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#e0e0e0'; setLensTooltip(null); }}
                    data-testid={`${app.id}-lens-btn`}
                  >
                    <ScanLine style={{ width: 14, height: 14, color: '#888888' }} />
                  </button>
                  {lensTooltip === app.id && (
                    <div style={{ position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: 6, background: '#111111', color: '#ffffff', fontSize: 11, padding: '4px 8px', borderRadius: 4, whiteSpace: 'nowrap', pointerEvents: 'none', fontWeight: 400 }}>
                      Set up with Lens file upload
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* ━━━ 7. FOOTER BAR ━━━ */}
      <footer className="flex-shrink-0 flex items-center justify-between" style={{ background: '#ffffff', borderTop: '0.5px solid #e8e8e8', padding: '12px 24px' }} data-testid="footer-bar">
        <div className="flex items-center" style={{ gap: 8 }}>
          <img src="/ariaone_logo_black.png" alt="AriaOne" style={{ height: 16, width: 'auto', objectFit: 'contain' }} data-testid="footer-logo" />
          <span style={{ fontSize: 11, color: '#888888', fontWeight: 400 }}>aria one</span>
          <span style={{ fontSize: 11, color: '#bbbbbb', fontWeight: 400 }}>Healthcare Platform v2.1</span>
        </div>
        <div className="flex items-center" style={{ gap: 0 }}>
          <span style={{ fontSize: 11, color: '#bbbbbb', fontWeight: 400 }}>Last login: {USER.lastLogin}</span>
          <div style={{ width: 1, height: 14, background: '#e0e0e0', margin: '0 10px' }} />
          <div className="flex items-center" style={{ gap: 5 }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e' }} />
            <span style={{ fontSize: 11, color: '#aaaaaa', fontWeight: 400 }}>All systems operational</span>
          </div>
        </div>
      </footer>

      <VideoTourDialog open={showVideoTour} onOpenChange={setShowVideoTour} />
    </div>
  );
};

export default LandingPage;
